import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Property, PropertyFilters } from '@/types/property';
import { buildStyleOrFilter, buildConditionOrFilter } from '@/lib/propertyStyleFilters';

// Keyword groups for distressed/foreclosure properties.
// CREA DDF does not expose a "bank-owned / foreclosure / probate" status field,
// so detection happens by scanning agent_name AND public-remarks (description).
const DISTRESS_BANK_KEYWORDS = [
  'bank owned', 'bank-owned', 'lender owned', 'lender-owned', 'reo',
  'power of sale', 'mortgage in possession', 'mortgagee sale',
  'atb financial', 'td bank', 'rbc', 'bmo', 'scotiabank', 'cibc',
  'national bank', 'hsbc', 'canada trust', 'credit union',
];
const DISTRESS_COURT_KEYWORDS = [
  'foreclosure', 'foreclos', "court of king's bench", 'court of kings bench',
  "king's bench", 'kings bench', 'judicial sale', 'judicial listing',
  'civil enforcement', 'sheriff sale', 'sheriff', 'court ordered', 'court order sale',
];
const DISTRESS_PROBATE_KEYWORDS = [
  'probate', 'estate sale', 'estate of', 'executor', 'executrix',
];
const DISTRESS_KEYWORDS = [
  ...DISTRESS_BANK_KEYWORDS,
  ...DISTRESS_COURT_KEYWORDS,
  ...DISTRESS_PROBATE_KEYWORDS,
];

function distressKeywordsFor(type?: 'bank' | 'court' | 'probate') {
  if (type === 'bank') return DISTRESS_BANK_KEYWORDS;
  if (type === 'court') return DISTRESS_COURT_KEYWORDS;
  if (type === 'probate') return DISTRESS_PROBATE_KEYWORDS;
  return DISTRESS_KEYWORDS;
}

/**
 * Apply the distressed filter using the precomputed flag columns.
 * The flags are generated in the database from the exact same keyword lists
 * above (agent_name OR description), so matching is unchanged — but the query
 * uses an index instead of scanning every listing's remarks.
 */
function applyDistressedFilter(query: any, type?: 'bank' | 'court' | 'probate') {
  if (type === 'bank') return query.eq('distress_bank', true);
  if (type === 'court') return query.eq('distress_court', true);
  if (type === 'probate') return query.eq('distress_probate', true);
  return query.or('distress_bank.eq.true,distress_court.eq.true,distress_probate.eq.true');
}

export function escapeLike(input: string) {
  // Escape characters that have special meaning in SQL LIKE patterns
  return input.replace(/[\\%_]/g, (m) => `\\${m}`);
}

function escapeRegex(input: string) {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Map of common address abbreviation synonyms. Each token in the query is
// expanded to all known equivalents so a search for "st" also matches "street".
const TOKEN_SYNONYMS: Record<string, string[]> = {
  st: ['st', 'street'],
  street: ['st', 'street'],
  ave: ['ave', 'av', 'avenue'],
  av: ['ave', 'av', 'avenue'],
  avenue: ['ave', 'av', 'avenue'],
  rd: ['rd', 'road'],
  road: ['rd', 'road'],
  dr: ['dr', 'drive'],
  drive: ['dr', 'drive'],
  blvd: ['blvd', 'boulevard'],
  boulevard: ['blvd', 'boulevard'],
  cres: ['cres', 'crescent'],
  crescent: ['cres', 'crescent'],
  ct: ['ct', 'court'],
  court: ['ct', 'court'],
  pl: ['pl', 'place'],
  place: ['pl', 'place'],
  ln: ['ln', 'lane'],
  lane: ['ln', 'lane'],
  way: ['way', 'wy'],
  wy: ['way', 'wy'],
  trl: ['trl', 'trail'],
  trail: ['trl', 'trail'],
  hwy: ['hwy', 'highway'],
  highway: ['hwy', 'highway'],
  pkwy: ['pkwy', 'parkway'],
  parkway: ['pkwy', 'parkway'],
  terr: ['terr', 'terrace'],
  terrace: ['terr', 'terrace'],
  gt: ['gt', 'gate'],
  gate: ['gt', 'gate'],
  grn: ['grn', 'green'],
  green: ['grn', 'green'],
  hts: ['hts', 'heights'],
  heights: ['hts', 'heights'],
  mnr: ['mnr', 'manor'],
  manor: ['mnr', 'manor'],
  pt: ['pt', 'point', 'pointe'],
  point: ['pt', 'point', 'pointe'],
  pointe: ['pt', 'point', 'pointe'],
  sq: ['sq', 'square'],
  square: ['sq', 'square'],
  cir: ['cir', 'circle'],
  circle: ['cir', 'circle'],
  cv: ['cv', 'cove'],
  cove: ['cv', 'cove'],
  bay: ['bay', 'by'],
  by: ['bay', 'by'],
  vw: ['vw', 'view'],
  view: ['vw', 'view'],
  vlg: ['vlg', 'village'],
  village: ['vlg', 'village'],
  ldg: ['ldg', 'landing'],
  landing: ['ldg', 'landing'],
  pk: ['pk', 'park'],
  park: ['pk', 'park'],
  nw: ['nw', 'northwest', 'n.w'],
  northwest: ['nw', 'northwest', 'n.w'],
  sw: ['sw', 'southwest', 's.w'],
  southwest: ['sw', 'southwest', 's.w'],
  ne: ['ne', 'northeast', 'n.e'],
  northeast: ['ne', 'northeast', 'n.e'],
  se: ['se', 'southeast', 's.e'],
  southeast: ['se', 'southeast', 's.e'],
  n: ['n', 'north'],
  north: ['n', 'north'],
  s: ['s', 'south'],
  south: ['s', 'south'],
  e: ['e', 'east'],
  east: ['e', 'east'],
  w: ['w', 'west'],
  west: ['w', 'west'],
  // Unit/suite prefixes are noise — expanded to nothing meaningful and
  // filtered out as stopwords below.
};

// Tokens we should drop entirely (unit prefixes, generic filler words).
const STOPWORDS = new Set([
  'unit', 'suite', 'ste', 'apt', 'apartment', '#', 'no', 'number',
  'the', 'and', 'of', 'in', 'at', 'on',
]);

const SEARCH_COLUMNS = ['address', 'title', 'city', 'neighborhood', 'postal_code', 'mls_id'];

/**
 * Normalize a free-text address query into clean searchable tokens.
 *
 * - Strips punctuation (#, ., ,, -, /, etc.)
 * - Splits digit/letter runs ("40st" → "40", "st")
 * - Lowercases everything
 * - Drops stopwords ("unit", "suite", "apt", etc.)
 */
export function tokenizePropertySearchQuery(searchQuery: string): string[] {
  // 1. Replace every non-alphanumeric char with a space (handles #, ., -, /, ,)
  const cleaned = searchQuery.replace(/[^a-zA-Z0-9]+/g, ' ').trim().toLowerCase();
  if (!cleaned) return [];

  // 2. Split on whitespace, then split each chunk between digits and letters
  //    so "40st" becomes ["40", "st"] and "st40" becomes ["st", "40"].
  const raw = cleaned.split(' ').flatMap((chunk) => chunk.match(/\d+|[a-z]+/g) ?? []);

  // 3. Drop stopwords
  return raw.filter((t) => t.length > 0 && !STOPWORDS.has(t));
}

/**
 * Tokenize a search query and return an array of OR-clause groups.
 * Each token must match at least one column (ANDed across token groups by
 * chaining .or() calls). Numeric tokens require word-boundary matches so
 * "2107" does not match inside "12107".
 */
export function buildOrGroupsForSearch(searchQuery: string): string[] {
  const tokens = tokenizePropertySearchQuery(searchQuery);
  if (tokens.length === 0) return [];

  return tokens.map((token) => {
    // Numeric tokens (street numbers, civic numbers, postal digits) MUST match
    // on word boundaries — otherwise "2107" matches inside "12107 53 ST NW".
    // Scope numeric tokens to address-like columns only.
    if (/^\d+$/.test(token)) {
      const pattern = `\\m${escapeRegex(token)}\\M`;
      return ['address', 'postal_code', 'mls_id']
        .map((col) => `${col}.imatch.${pattern}`)
        .join(',');
    }
    const variants = TOKEN_SYNONYMS[token] ?? [token];
    return variants
      .flatMap((v) => SEARCH_COLUMNS.map((col) => `${col}.ilike.*${v}*`))
      .join(',');
  });
}

// Slim columns for card-only displays — skips heavy `description`/`features`.
const PROPERTY_CARD_COLS =
  'id, slug, title, address, city, neighborhood, price, bedrooms, bathrooms, sqft, property_type, images, listing_date, latitude, longitude, status, agent_name, brokerage_name';

export function useProperties(filters?: PropertyFilters, limit?: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['properties', filters, limit],
    enabled: options?.enabled ?? true,
    queryFn: async () => {
      let query = supabase
        .from('properties')
        .select(PROPERTY_CARD_COLS)
        .eq('status', 'Active')
        .gt('price', 0) // Exclude $0 listings (data issue)
        .not('images', 'is', null)
        .not('images', 'eq', '{}')
        .order('listing_date', { ascending: false });

      if (filters?.city) {
        query = query.ilike('city', `${escapeLike(filters.city)}%`);
      }
      if (filters?.neighborhood) {
        // Handle "City - Neighborhood" format from search bar (e.g., "Edmonton - Downtown")
        const neighborhoodNameRaw = filters.neighborhood.includes(' - ')
          ? filters.neighborhood.split(' - ')[1]
          : filters.neighborhood;

        // Normalize the input; use wildcard match to tolerate data with extra whitespace
        // (counts are based on normalized values, but the stored rows may contain extra spaces).
        const neighborhoodName = neighborhoodNameRaw.replace(/\s+/g, ' ').trim();
        query = query.ilike('neighborhood', `%${escapeLike(neighborhoodName)}%`);
      }
      // Filter by multiple neighborhoods (for hub pages)
      if (filters?.neighborhoods && filters.neighborhoods.length > 0) {
        const orConditions = filters.neighborhoods
          .map((n) => `neighborhood.eq.${n}`)
          .join(',');
        query = query.or(orConditions);
      }
      if (filters?.minPrice) {
        query = query.gte('price', filters.minPrice);
      }
      if (filters?.maxPrice) {
        query = query.lte('price', filters.maxPrice);
      }
      if (filters?.propertyType) {
        query = query.eq('property_type', filters.propertyType);
      }
      if (filters?.minBedrooms) {
        query = query.gte('bedrooms', filters.minBedrooms);
      }
      if (filters?.maxBedrooms) {
        query = query.lte('bedrooms', filters.maxBedrooms);
      }
      if (filters?.minBathrooms) {
        query = query.gte('bathrooms', filters.minBathrooms);
      }
      if (filters?.minSqft) {
        query = query.gte('sqft', filters.minSqft);
      }
      if (filters?.maxSqft) {
        query = query.lte('sqft', filters.maxSqft);
      }
      if (filters?.minYearBuilt) {
        query = query.gte('year_built', filters.minYearBuilt);
      }
      if (filters?.maxYearBuilt) {
        query = query.lte('year_built', filters.maxYearBuilt);
      }
      if (filters?.parking) {
        query = query.ilike('parking', `%${escapeLike(filters.parking)}%`);
      }
      if (filters?.searchQuery) {
        for (const group of buildOrGroupsForSearch(filters.searchQuery)) {
          query = query.or(group);
        }
      }
      if (filters?.distressed) {
        query = applyDistressedFilter(query, filters.distressedType);
      }
      {
        const styleOr = buildStyleOrFilter(filters?.architecturalStyles);
        if (styleOr) query = query.or(styleOr);
        const conditionOr = buildConditionOrFilter(filters?.propertyConditions);
        if (conditionOr) query = query.or(conditionOr);
      }
      if (limit) {
        query = query.limit(limit);
      } else {
        query = query.limit(500);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as unknown as Property[];
    },
  });
}

// Get the total count of properties matching filters
export function usePropertiesCount(filters?: PropertyFilters) {
  return useQuery({
    // Counts are cached for a while so repeated searches reuse the result
    // instead of re-running the heaviest query on the page.
    queryKey: ['properties-count', filters],
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    queryFn: async () => {
      let query = supabase
        .from('properties')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'Active')
        .gt('price', 0) // Exclude $0 listings
        .not('images', 'is', null)
        .not('images', 'eq', '{}');

      if (filters?.city) {
        query = query.ilike('city', `${escapeLike(filters.city)}%`);
      }
      if (filters?.neighborhood) {
        const neighborhoodNameRaw = filters.neighborhood.includes(' - ')
          ? filters.neighborhood.split(' - ')[1]
          : filters.neighborhood;

        const neighborhoodName = neighborhoodNameRaw.replace(/\s+/g, ' ').trim();
        query = query.ilike('neighborhood', `%${escapeLike(neighborhoodName)}%`);
      }
      // Filter by multiple neighborhoods (for hub pages)
      if (filters?.neighborhoods && filters.neighborhoods.length > 0) {
        const orConditions = filters.neighborhoods
          .map((n) => `neighborhood.eq.${n}`)
          .join(',');
        query = query.or(orConditions);
      }
      if (filters?.minPrice) {
        query = query.gte('price', filters.minPrice);
      }
      if (filters?.maxPrice) {
        query = query.lte('price', filters.maxPrice);
      }
      if (filters?.propertyType) {
        query = query.eq('property_type', filters.propertyType);
      }
      if (filters?.minBedrooms) {
        query = query.gte('bedrooms', filters.minBedrooms);
      }
      if (filters?.maxBedrooms) {
        query = query.lte('bedrooms', filters.maxBedrooms);
      }
      if (filters?.minBathrooms) {
        query = query.gte('bathrooms', filters.minBathrooms);
      }
      if (filters?.minSqft) {
        query = query.gte('sqft', filters.minSqft);
      }
      if (filters?.maxSqft) {
        query = query.lte('sqft', filters.maxSqft);
      }
      if (filters?.minYearBuilt) {
        query = query.gte('year_built', filters.minYearBuilt);
      }
      if (filters?.maxYearBuilt) {
        query = query.lte('year_built', filters.maxYearBuilt);
      }
      if (filters?.parking) {
        query = query.ilike('parking', `%${escapeLike(filters.parking)}%`);
      }
      if (filters?.searchQuery) {
        for (const group of buildOrGroupsForSearch(filters.searchQuery)) {
          query = query.or(group);
        }
      }
      if (filters?.distressed) {
        query = applyDistressedFilter(query, filters.distressedType);
      }
      {
        const styleOr = buildStyleOrFilter(filters?.architecturalStyles);
        if (styleOr) query = query.or(styleOr);
        const conditionOr = buildConditionOrFilter(filters?.propertyConditions);
        if (conditionOr) query = query.or(conditionOr);
      }

      const { count, error } = await query;

      if (error) throw error;
      return count ?? 0;
    },
  });
}

/** Fetches a property by UUID or slug */
export function useProperty(idOrSlug: string) {
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrSlug);
  
  return useQuery({
    queryKey: ['property', idOrSlug],
    queryFn: async () => {
      const column = isUuid ? 'id' : 'slug';
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq(column, idOrSlug)
        .maybeSingle();
      
      if (error) throw error;
      return data as Property | null;
    },
    enabled: !!idOrSlug,
  });
}


export function useFeaturedProperties(city?: string, limit = 6) {
  return useQuery({
    queryKey: ['featured-properties', city, limit],
    queryFn: async () => {
      let query = supabase
        .from('properties')
        .select(PROPERTY_CARD_COLS)
        .eq('status', 'Active')
        .gt('price', 0) // Exclude $0 listings
        .not('images', 'is', null) // Only show properties with images
        .not('images', 'eq', '{}')
        .order('listing_date', { ascending: false })
        .limit(limit);
      
      if (city) {
        query = query.ilike('city', `%${city}%`);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as unknown as Property[];
    },
    staleTime: 1000 * 60 * 2, // Consider fresh for 2 minutes
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
  });
}

// ----- Infinite-scroll variant ---------------------------------------------

export type PropertySortOption = 'newest' | 'price-asc' | 'price-desc' | 'beds-desc';

const INFINITE_PAGE_SIZE = 24;

/**
 * Apply the standard PropertyFilters block to a Supabase query builder.
 * Mirrors the logic in `useProperties` / `usePropertiesCount` so all three
 * code paths stay in sync. Returns the (mutated) query builder.
 */
function applyPropertyFilters<Q extends ReturnType<typeof supabase.from>>(query: any, filters?: PropertyFilters) {
  if (filters?.city) {
    query = query.ilike('city', `${escapeLike(filters.city)}%`);
  }
  if (filters?.neighborhood) {
    const neighborhoodNameRaw = filters.neighborhood.includes(' - ')
      ? filters.neighborhood.split(' - ')[1]
      : filters.neighborhood;
    const neighborhoodName = neighborhoodNameRaw.replace(/\s+/g, ' ').trim();
    query = query.ilike('neighborhood', `%${escapeLike(neighborhoodName)}%`);
  }
  if (filters?.neighborhoods && filters.neighborhoods.length > 0) {
    const orConditions = filters.neighborhoods.map((n) => `neighborhood.eq.${n}`).join(',');
    query = query.or(orConditions);
  }
  if (filters?.minPrice) query = query.gte('price', filters.minPrice);
  if (filters?.maxPrice) query = query.lte('price', filters.maxPrice);
  if (filters?.propertyType) query = query.eq('property_type', filters.propertyType);
  if (filters?.minBedrooms) query = query.gte('bedrooms', filters.minBedrooms);
  if (filters?.maxBedrooms) query = query.lte('bedrooms', filters.maxBedrooms);
  if (filters?.minBathrooms) query = query.gte('bathrooms', filters.minBathrooms);
  if (filters?.minSqft) query = query.gte('sqft', filters.minSqft);
  if (filters?.maxSqft) query = query.lte('sqft', filters.maxSqft);
  if (filters?.minYearBuilt) query = query.gte('year_built', filters.minYearBuilt);
  if (filters?.maxYearBuilt) query = query.lte('year_built', filters.maxYearBuilt);
  if (filters?.parking) query = query.ilike('parking', `%${escapeLike(filters.parking)}%`);
  if (filters?.searchQuery) {
    for (const group of buildOrGroupsForSearch(filters.searchQuery)) {
      query = query.or(group);
    }
  }
  if (filters?.distressed) {
    query = applyDistressedFilter(query, filters.distressedType);
  }
  const styleOr = buildStyleOrFilter(filters?.architecturalStyles);
  if (styleOr) query = query.or(styleOr);
  const conditionOr = buildConditionOrFilter(filters?.propertyConditions);
  if (conditionOr) query = query.or(conditionOr);
  return query;
}

const SORT_CONFIG: Record<PropertySortOption, { column: 'listing_date' | 'price' | 'bedrooms'; ascending: boolean }> = {
  newest: { column: 'listing_date', ascending: false },
  'price-asc': { column: 'price', ascending: true },
  'price-desc': { column: 'price', ascending: false },
  'beds-desc': { column: 'bedrooms', ascending: false },
};

function applySort(query: any, sortBy: PropertySortOption) {
  const { column, ascending } = SORT_CONFIG[sortBy] ?? SORT_CONFIG.newest;
  // `id` is a stable tiebreaker so keyset pages never repeat or skip a row.
  return query.order(column, { ascending, nullsFirst: false }).order('id', { ascending: true });
}

/** Cursor for keyset pagination, including the NULL-value tail. */
type PropertyCursor = {
  value: string | number | null;
  id: string | null;
};

const INITIAL_CURSOR: PropertyCursor = { value: null, id: null };

/**
 * Paginated property list for infinite scroll using cursor (keyset) pagination:
 * each page asks for rows after the previous page's last sort value + id, so
 * deep pages cost the same as the first one (no growing OFFSET scan).
 */
export function useInfiniteProperties(filters: PropertyFilters | undefined, sortBy: PropertySortOption = 'newest') {
  return useInfiniteQuery({
    queryKey: ['properties-infinite', filters, sortBy],
    initialPageParam: INITIAL_CURSOR,
    queryFn: async ({ pageParam }) => {
      const cursor = pageParam as PropertyCursor;
      const { column, ascending } = SORT_CONFIG[sortBy] ?? SORT_CONFIG.newest;

      let query = supabase
        .from('properties')
        .select(PROPERTY_CARD_COLS)
        .eq('status', 'Active')
        .gt('price', 0)
        .not('images', 'is', null)
        .not('images', 'eq', '{}');

      query = applyPropertyFilters(query, filters);
      query = applySort(query, sortBy);

      if (cursor.id && cursor.value !== null && cursor.value !== undefined) {
        const op = ascending ? 'gt' : 'lt';
        // Quote non-numeric values (timestamps) so PostgREST parses them safely.
        const v = typeof cursor.value === 'number' ? `${cursor.value}` : `"${cursor.value}"`;
        query = query.or(
          `${column}.${op}.${v},and(${column}.eq.${v},id.gt.${cursor.id}),${column}.is.null`,
        );
      } else if (cursor.id) {
        // Once inside the NULL tail, continue by the stable id tiebreaker.
        query = query.is(column, null).gt('id', cursor.id);
      }
      query = query.limit(INFINITE_PAGE_SIZE);

      const { data, error } = await query;
      if (error) throw error;

      const rows = (data ?? []) as unknown as Property[];
      const last = rows[rows.length - 1];
      const lastValue = last ? ((last as any)[column] ?? null) : null;

      const nextCursor: PropertyCursor = {
        value: lastValue,
        id: last?.id ?? null,
      };

      return { rows, nextCursor };
    },
    getNextPageParam: (lastPage) =>
      lastPage.rows.length < INFINITE_PAGE_SIZE ? undefined : lastPage.nextCursor,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
  });
}
